import {
  AbstractMesh,
  Vector3,
  Scene,
  ArcRotateCamera,
  Observer,
  InstancedMesh,
} from '@babylonjs/core'
import { SceneManager } from './sceneManager'
import { MaterialManager } from './materials/materialManager'
import { BuildingElementType } from '../models/Construction'
import { CodeValidationDiscipline } from '../models/Validation'
import { EventType } from '../utils/EventEmitter'

export class ClusteringManager {
  private static instance: ClusteringManager | null
  private sceneManager: SceneManager
  private materialManager: MaterialManager
  private scene: Scene
  private camera: ArcRotateCamera

  private clusterMinSize = 2
  private lastClusterKeys: Set<string> = new Set()

  private updateClustersObserver: Map<CodeValidationDiscipline, Observer<any>> =
    new Map<CodeValidationDiscipline, Observer<any>>()
  private clusterViewUpdatedObserver: Observer<any> | null = null

  private constructor() {
    this.sceneManager = SceneManager.getInstance()
    this.materialManager = MaterialManager.getInstance()
    const scene = this.sceneManager.getScene()
    const camera = this.sceneManager.getCamera()
    if (!scene || !camera) {
      throw new Error()
    }

    this.scene = scene
    this.camera = camera

    let rotateTimeout: NodeJS.Timeout | null = null
    let lastAlpha = this.camera.alpha
    let lastBeta = this.camera.beta
    let isRotating = false

    this.clusterViewUpdatedObserver = this.scene.onBeforeRenderObservable.add(
      () => {
        const alphaChanged = Math.abs(this.camera.alpha - lastAlpha) > 0.01
        const betaChanged = Math.abs(this.camera.beta - lastBeta) > 0.01

        // Handle rotation
        if (alphaChanged || betaChanged) {
          lastAlpha = this.camera.alpha
          lastBeta = this.camera.beta
          isRotating = true

          if (rotateTimeout) clearTimeout(rotateTimeout)

          rotateTimeout = setTimeout(() => {
            const stillRotating =
              Math.abs(this.camera.alpha - lastAlpha) > 0.01 ||
              Math.abs(this.camera.beta - lastBeta) > 0.01

            if (!stillRotating && isRotating) {
              isRotating = false
              SceneManager.getInstance()
                .getEventEmitter()
                ?.emit(EventType.CodeValidationClustersViewUpdated)
            }
          }, 0)
        }
      },
    )
  }

  static getInstance(): ClusteringManager {
    if (!ClusteringManager.instance) {
      ClusteringManager.instance = new ClusteringManager()
    }

    return ClusteringManager.instance
  }

  static dispose() {
    ClusteringManager.instance?.eraseClusters()
    ClusteringManager.instance = null
  }

  createClusters(
    discipline: CodeValidationDiscipline,
    ids: (string | undefined)[],
  ) {
    if (this.updateClustersObserver.has(discipline)) {
      this.scene.onBeforeRenderObservable.remove(
        this.updateClustersObserver.get(discipline)!,
      )
    }

    const meshes: AbstractMesh[] = []
    for (const elementId of ids) {
      if (!elementId) {
        continue
      }

      const mesh = this.scene?.getMeshById(elementId)
      const meshType: BuildingElementType = mesh?.metadata?.type

      if (mesh && meshType) {
        meshes.push(mesh)
      }
    }

    let lastCameraRadius = this.camera.radius
    let zoomTimeout: NodeJS.Timeout | null = null
    let isZooming = false

    this.updateClustersObserver.set(
      discipline,
      this.scene.onBeforeRenderObservable.add(() => {
        const radiusChanged =
          Math.abs(this.camera.radius - lastCameraRadius) > 0.01

        // Handle zoom
        if (radiusChanged) {
          lastCameraRadius = this.camera.radius
          isZooming = true

          if (zoomTimeout) clearTimeout(zoomTimeout)

          zoomTimeout = setTimeout(() => {
            const stillZooming =
              Math.abs(this.camera.radius - lastCameraRadius) > 0.01
            if (!stillZooming && isZooming) {
              isZooming = false
              const clusters = this.updateClusters(discipline, meshes)
              SceneManager.getInstance()
                .getEventEmitter()
                ?.emit(EventType.CodeValidationClustersUpdated, clusters)
            }
          }, 0)
        }
      }),
    )

    return this.updateClusters(discipline, meshes)
  }

  eraseClusters() {
    if (this.updateClustersObserver.size) {
      this.updateClustersObserver.forEach((value, key) => {
        this.scene.onBeforeRenderObservable.remove(value)
      })

      this.updateClustersObserver.clear()
    }

    if (this.clusterViewUpdatedObserver) {
      this.scene.onBeforeRenderObservable.remove(
        this.clusterViewUpdatedObserver,
      )
    }
  }

  private updateClusters(
    discipline: CodeValidationDiscipline,
    meshes: AbstractMesh[],
  ) {
    const cameraPos = this.camera.position
    const gridSize = this.getDynamicGridSize()
    const rawClusters: Map<string, AbstractMesh[]> = this.clusterMeshes(
      meshes,
      gridSize,
    )
    const clusters: Map<
      string,
      { meshes: AbstractMesh[]; isVisible: boolean }
    > = new Map()
    const activeKeys = new Set<string>()

    rawClusters.forEach((cluster, key) => {
      const center = cluster
        .reduce(
          (acc, mesh) => acc.add(mesh.getAbsolutePosition()),
          Vector3.Zero(),
        )
        .scale(1 / cluster.length)
      const distance = Vector3.Distance(cameraPos, center)
      const isVisible = !(
        cluster.length <= this.clusterMinSize || distance < 400
      )

      if (!isVisible) {
        cluster
          .filter((mesh) => !(mesh instanceof InstancedMesh))
          .forEach((mesh) => {
            mesh.material = this.materialManager.getCriticalMaterial(
              mesh.metadata.type,
            )
          })
      } else {
        cluster
          .filter((mesh) => !(mesh instanceof InstancedMesh))
          .forEach((mesh) => {
            mesh.material = mesh.metadata.isTransparent
              ? this.materialManager.getInactiveMaterial(mesh.metadata.type)
              : this.materialManager.getNormalMaterial(mesh.metadata.type)
          })
        activeKeys.add(key)
      }

      clusters.set(key, { meshes: cluster, isVisible })
    })

    if (!this.hasClusterChanged(activeKeys)) return null
    this.lastClusterKeys = activeKeys

    return {
      discipline,
      clusters: Array.from(clusters.values()),
    }
  }

  private clusterMeshes(meshes: AbstractMesh[], gridSize: number) {
    if (this.camera.radius > 1400) {
      return new Map([['global', meshes]])
    }

    const clusters = new Map<string, AbstractMesh[]>()

    meshes.forEach((mesh) => {
      const pos = mesh.getAbsolutePosition()
      const key = `${Math.floor(pos.x / gridSize)}_${Math.floor(pos.z / gridSize)}_${Math.floor(pos.y / gridSize)}`

      if (!clusters.has(key)) {
        clusters.set(key, [])
      }
      clusters.get(key)?.push(mesh)
    })

    return clusters
  }

  private hasClusterChanged(newKeys: Set<string>) {
    if (newKeys.size !== this.lastClusterKeys.size) return true
    newKeys.forEach((key) => {
      if (!this.lastClusterKeys.has(key)) return true
    })
    return false
  }

  private getDynamicGridSize() {
    const scaleFactor = 0.3
    return Math.max(10, this.camera.radius * scaleFactor)
  }
}
